/**
 * Purchase Order Service (Formal PO Workflow — Issue #130 / #106)
 *
 * Frontend service for the dual purchase model. Implements Flow B — the
 * formal PO state machine:
 *
 *   draft → pending_approval → approved → sent → received → invoiced
 *                                          ↓
 *                                      cancelled (terminal)
 *
 * Pattern:
 *   - READs go through Supabase directly (RLS already filters)
 *   - WRITEs go through the MCP server so audit logs + notifications
 *     fire from the same code path the agent uses.
 *
 * Flow A (legacy createPurchase, status=invoiced immediately) lives in
 * purchaseService.js and is unchanged.
 *
 * @module services/purchaseOrderService
 */

import { supabase } from './supabase';
import { callMcpTool } from './mcpClient';

// ============================================================
// Extended status machine (Flow B)
// ============================================================

/**
 * Estados válidos de `purchases.status` para el flujo formal.
 *
 * NO HAY 'pending' — misma razón que ya se documentó en
 * `purchaseService.PURCHASE_STATUS` (commit a8d0420). El CHECK real en
 * producción es
 *
 *   purchases_status_check CHECK (status = ANY (ARRAY[
 *     'draft','pending_approval','approved','sent',
 *     'received','partial','invoiced','cancelled']))
 *
 * Este archivo era la copia que quedó viva: declaraba `PENDING: 'pending'`
 * con el comentario "kept here so the badge map covers every value". El
 * comentario era falso — un mapa de badges no cubre nada si la base rechaza
 * el valor, y `SELECT status, count(*) FROM purchases GROUP BY status`
 * tampoco devuelve filas viejas que pintar (verificado 2026-07-28).
 */
export const PO_STATUS = {
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  SENT: 'sent',
  RECEIVED: 'received',
  PARTIAL: 'partial',
  INVOICED: 'invoiced',
  CANCELLED: 'cancelled',
};

export const PO_STATUS_LABELS = {
  draft: 'Borrador',
  pending_approval: 'Pend. Aprobación',
  approved: 'Aprobada',
  sent: 'Enviada al Proveedor',
  received: 'Recibida',
  partial: 'Parcial',
  invoiced: 'Facturada',
  cancelled: 'Cancelada',
};

/**
 * Bootstrap variant for the Badge component. Tuned to match #130 spec:
 *   draft → gris, pending_approval → amarillo, approved → azul,
 *   sent → cyan (info), received → verde claro, invoiced → verde,
 *   cancelled → rojo.
 */
export const PO_STATUS_VARIANTS = {
  draft: 'secondary',
  pending_approval: 'warning',
  approved: 'primary',
  sent: 'info',
  received: 'success',
  partial: 'warning',
  invoiced: 'success',
  cancelled: 'danger',
};

/**
 * Icon glyph per status, used in lists and badges.
 */
export const PO_STATUS_ICONS = {
  draft: 'bi-pencil-square',
  pending_approval: 'bi-hourglass-split',
  approved: 'bi-check2-circle',
  sent: 'bi-send',
  received: 'bi-box-arrow-in-down',
  partial: 'bi-box-arrow-in-down',
  invoiced: 'bi-receipt',
  cancelled: 'bi-x-circle',
};

// ============================================================
// READ — direct Supabase
// ============================================================

/**
 * List POs filtered by a list of statuses (used by the admin inbox).
 */
export const listPOsByStatus = async (statuses = []) => {
  try {
    if (!statuses || statuses.length === 0) {
      return { data: [], error: null };
    }
    const { data, error } = await supabase
      .from('purchases')
      .select(`
        *,
        supplier:suppliers(id, name),
        creator:profiles!created_by(id, full_name),
        items:purchase_items(
          id, inventory_id, description, quantity_ordered, quantity_received,
          unit_cost, lot_number, expiration_date, status,
          inventory:inventory(id, name, sku, unit)
        )
      `)
      .in('status', statuses)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('listPOsByStatus error:', error);
    return { data: [], error };
  }
};

export const listPendingApprovalPOs = () =>
  listPOsByStatus(['pending_approval']);

/**
 * ¿Puede el usuario actual aprobar compras, y de cuáles proyectos?
 *
 * **Desde #550 (Luis, 2026-08-07): aprueba el admin y nadie más.**
 *
 * Esta función leía `project_leaders.can_approve_purchases` para armar el
 * ámbito del líder delegado que pidió Jorge el 2026-07-28. Ese camino se
 * cortó, y este archivo era el que MÁS fácil se quedaba atrás: no llama a
 * `can_approve_purchase()` sino que replica la regla contra la tabla, así
 * que la migración 20260807120000 (que redefine la función a admin-only)
 * NO lo alcanza. Un supervisor con el flag viejo prendido habría seguido
 * viendo el inbox y los botones de aprobar, y recién habría descubierto que
 * no puede al chocar contra el trigger — el peor de los dos mundos, porque
 * parece que el sistema está roto en vez de que la regla cambió.
 *
 * Se conserva la FORMA del retorno (`projectIds`) para no tocar a los dos
 * consumidores (AdminPOInbox, PurchaseDetail) y para que, si alguna vez
 * vuelve una regla por proyecto, vuelva acá adentro y no en las pantallas.
 *
 * Esto sigue siendo PINTURA. Quien manda es la base: la policy
 * `purchases_delegated_leader_approve` y el trigger
 * `trg_enforce_purchase_approval`, ambos apoyados en
 * `public.can_approve_purchase()`.
 *
 * @returns {Promise<{ isAdmin: boolean, projectIds: string[], canApproveAny: boolean, error: Error|null }>}
 */
export const getMyPurchaseApprovalScope = async () => {
  try {
    const { data: { user }, error: userErr } = await supabase.auth.getUser();
    if (userErr) throw userErr;
    if (!user) return { isAdmin: false, projectIds: [], canApproveAny: false, error: null };

    const { data: profile, error: profileErr } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .maybeSingle();
    if (profileErr) throw profileErr;

    const isAdmin = profile?.role === 'admin';
    // #550: sin rama delegada. Si no es admin, no aprueba nada, en ningún
    // proyecto. `projectIds` queda vacío a propósito y no por un fallo.
    return {
      isAdmin,
      projectIds: [],
      canApproveAny: isAdmin,
      error: null
    };
  } catch (error) {
    console.error('getMyPurchaseApprovalScope error:', error);
    // Fallar cerrado: si no se puede determinar, no se pinta el inbox.
    return { isAdmin: false, projectIds: [], canApproveAny: false, error };
  }
};

export const listAwaitingReceiptPOs = () =>
  listPOsByStatus(['sent', 'partial']);

// ============================================================
// WRITE — through MCP
// ============================================================

/**
 * Create a draft PO (Flow B). NO invoice number/date — those land later
 * via attachInvoiceToPurchase. Items: [{inventory_id?, description?, quantity, unit_cost}]
 */
export const createPurchaseOrder = ({
  supplier_id,
  project_id,
  po_date,
  expected_delivery_date,
  currency = 'CRC',
  location,
  notes,
  items
}) =>
  callMcpTool('create_purchase_order', {
    supplier_id,
    // Este proxy NO reenviaba `project_id`, y la tool lo acepta y lo documenta
    // desde #225/#397. Consecuencia medida: TODA orden de compra creada desde
    // la web nacía con `project_id = NULL`.
    //
    // Eso no es cosmético: la policy `purchases_delegated_leader_approve` exige
    // `project_id IS NOT NULL`, y `can_approve_purchase(p_project_id)` devuelve
    // false cuando el proyecto es NULL — es deliberado, no se puede delegar
    // "por proyecto" algo que no tiene proyecto. O sea que la aprobación
    // delegable por líder que se acaba de construir (decisión de Jorge) no
    // aplicaba a NINGUNA orden creada desde la GUI. Sólo el admin podía
    // aprobarlas, en silencio y sin que nada lo dijera.
    //
    // Se manda `undefined` y no `null` cuando no viene: el schema de la tool
    // valida `format: uuid`, y un null explícito lo haría rebotar.
    project_id: project_id || undefined,
    po_date: po_date || undefined,
    expected_delivery_date: expected_delivery_date || undefined,
    currency,
    location: location || undefined,
    notes: notes || undefined,
    items
  });

/** draft → pending_approval */
export const submitPOForApproval = (purchase_id) =>
  callMcpTool('submit_po_for_approval', { purchase_id });

/** pending_approval → approved */
export const approvePurchaseOrder = (purchase_id) =>
  callMcpTool('approve_purchase_order', { purchase_id });

/** any pre-received status → cancelled */
export const rejectPurchaseOrder = (purchase_id, reason) =>
  callMcpTool('reject_purchase_order', { purchase_id, reason });

/** approved → sent (stamps sent_at) */
export const sendPOToSupplier = (purchase_id) =>
  callMcpTool('send_po_to_supplier', { purchase_id });

/** sent|partial → received, creates inventory batches */
export const receivePurchase = ({ purchase_id, received_items, received_date }) =>
  callMcpTool('receive_purchase', {
    purchase_id,
    received_items,
    received_date: received_date || undefined
  });

/** received → invoiced (closes the loop) */
export const attachInvoiceToPurchase = ({ purchase_id, invoice_number, invoice_date }) =>
  callMcpTool('attach_invoice_to_purchase', {
    purchase_id,
    invoice_number,
    invoice_date
  });

// ============================================================
// Helpers
// ============================================================

/**
 * Returns the list of contextual actions available for a PO given its
 * current status. Used by PurchaseDetail and AdminPOInbox to keep the
 * button rendering rules in one place.
 *
 * Each entry: { key, label, variant, icon }
 */
export const getContextualActions = (status) => {
  switch (status) {
    case PO_STATUS.DRAFT:
      return [
        { key: 'submit', label: 'Enviar para aprobación', variant: 'primary', icon: 'bi-send' },
        { key: 'reject', label: 'Cancelar PO', variant: 'outline-danger', icon: 'bi-x-circle' }
      ];
    case PO_STATUS.PENDING_APPROVAL:
      return [
        { key: 'approve', label: 'Aprobar', variant: 'success', icon: 'bi-check2-circle' },
        { key: 'reject', label: 'Rechazar', variant: 'outline-danger', icon: 'bi-x-circle' }
      ];
    case PO_STATUS.APPROVED:
      return [
        { key: 'send', label: 'Marcar como enviada al proveedor', variant: 'primary', icon: 'bi-send' },
        { key: 'reject', label: 'Cancelar PO', variant: 'outline-danger', icon: 'bi-x-circle' }
      ];
    case PO_STATUS.SENT:
    case PO_STATUS.PARTIAL:
      return [
        { key: 'receive', label: 'Recibir mercadería', variant: 'success', icon: 'bi-box-arrow-in-down' },
        { key: 'reject', label: 'Cancelar PO', variant: 'outline-danger', icon: 'bi-x-circle' }
      ];
    case PO_STATUS.RECEIVED:
      return [
        { key: 'invoice', label: 'Adjuntar factura', variant: 'primary', icon: 'bi-receipt' }
      ];
    case PO_STATUS.INVOICED:
    case PO_STATUS.CANCELLED:
    default:
      return [];
  }
};

const purchaseOrderService = {
  PO_STATUS,
  PO_STATUS_LABELS,
  PO_STATUS_VARIANTS,
  PO_STATUS_ICONS,
  listPOsByStatus,
  listPendingApprovalPOs,
  listAwaitingReceiptPOs,
  createPurchaseOrder,
  submitPOForApproval,
  approvePurchaseOrder,
  rejectPurchaseOrder,
  sendPOToSupplier,
  receivePurchase,
  attachInvoiceToPurchase,
  getContextualActions
};

export default purchaseOrderService;
